'use client';

import { useCallback, useRef } from 'react';
import { useCart, CartItem } from '@/lib/hooks/useCart';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import {
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  DrawerClose,
  DrawerContent,
} from '@/components/ui/drawer';
import { Package, Trash2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

export function CartDrawer() {
  const { cart, updateQuantity, removeItem } = useCart();
  const router = useRouter();
  const removedItemsCount = useRef(0);
  const removeTimeout = useRef<NodeJS.Timeout>();

  const handleGoToCart = useCallback(() => {
    router.push('/cart');
  }, [router]);

  const handleUpdateQuantity = useCallback(async (itemId: string, newQuantity: number) => {
    try {
      if (newQuantity < 1) return;

      const item = cart?.items.find(i => i.id === itemId);
      if (!item) return;

      const maxFromStock = item.product.pcs;
      const fromStock = Math.min(newQuantity, maxFromStock);
      const fromDelivery = newQuantity > maxFromStock ? newQuantity - maxFromStock : 0;

      await updateQuantity(itemId, newQuantity, fromStock, fromDelivery);
    } catch (error) {
      toast.error('Nie udało się zaktualizować ilości');
    }
  }, [updateQuantity, cart]);

  const handleRemoveItem = useCallback(async (itemId: string) => {
    try {
      await removeItem(itemId);
      removedItemsCount.current += 1;
      
      if (removeTimeout.current) {
        clearTimeout(removeTimeout.current);
      }
      
      removeTimeout.current = setTimeout(() => {
        if (removedItemsCount.current === 1) {
          toast.success('Usunięto produkt z koszyka');
        } else if (removedItemsCount.current > 1) {
          const suffix = removedItemsCount.current > 4 ? 'produktów' : 'produkty';
          toast.success(`Usunięto ${removedItemsCount.current} ${suffix} z koszyka`);
        }
        removedItemsCount.current = 0;
      }, 2000);
    } catch (error) {
      toast.error('Nie udało się usunąć produktu');
    }
  }, [removeItem]);

  const renderDeliveryInfo = (item: CartItem) => {
    if (item.fromDelivery > 0) {
      return (
        <div className="text-xs text-muted-foreground mt-1">
          {item.fromStock} szt. ze stanu
          <br />
          {item.fromDelivery} szt. z dostawy
          {item.product.deliveryDates && item.product.deliveryDates.length > 0 && (
            <> ({new Date(item.product.deliveryDates[0].date).toLocaleDateString()})</>
          )}
          {item.product.customDeliveryDate && (
            <> ({item.product.customDeliveryDate})</>
          )}
        </div>
      );
    }
    return null;
  };

  const itemCount = cart?.items?.length ?? 0;
  const cartTotal = cart?.items?.reduce((sum, item) => sum + (item.product.price * item.quantity), 0) ?? 0;

  const hasItems = !!cart?.items && cart.items.length > 0;

  return (
    <DrawerContent>
      <DrawerHeader className="px-4 pb-4 pt-5">
        <DrawerTitle>Twój koszyk</DrawerTitle>
        <DrawerDescription>
          {itemCount > 0
            ? `${itemCount} ${itemCount === 1 ? 'produkt' : 'produkty'} w koszyku`
            : 'Koszyk jest pusty'}
        </DrawerDescription>
      </DrawerHeader>
      {hasItems && (
        <div className="px-4 py-3 border-b bg-background space-y-2">
          <div className="flex justify-between">
            <span className="font-medium">Suma:</span>
            <span className="font-bold">{cartTotal.toFixed(2)} zł</span>
          </div>
          <DrawerClose asChild>
            <Button className="w-full" onClick={handleGoToCart}>
              Przejdź do koszyka
            </Button>
          </DrawerClose>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {hasItems ? (
          <div className="space-y-4">
            {cart!.items.map((item) => (
              <div key={item.id} className="flex items-center justify-between space-x-4 bg-white rounded-lg p-2">
                <div className="flex items-center space-x-4">
                  {item.product.image ? (
                    <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center">
                      <Package className="h-6 w-6 text-gray-400" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center">
                      <Package className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{item.product.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {item.product.price.toFixed(2)} zł
                    </p>
                    {renderDeliveryInfo(item)}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >
                    -
                  </Button>
                  <span className="w-8 text-center">{item.quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                  >
                    +
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleRemoveItem(item.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Twój koszyk jest pusty</p>
          </div>
        )}
      </div>
      <DrawerFooter className="pt-2 px-4 pb-4">
        <DrawerClose asChild>
          <Button variant="outline" className="w-full">
            Zamknij
          </Button>
        </DrawerClose>
      </DrawerFooter>
    </DrawerContent>
  );
} 